import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useUserSubject } from '@/auth/authStore';
import { LearningDemoEntry } from '@/pages/lms-demo/LearningDemoEntry';
import { api } from '@/lib/api';
import { PORTAL_NUMBER_LOCALE, type PortalTranslate } from '@/lib/i18n/portalLocale';
import { usePortalLocale } from '@/lib/i18n/PortalLocaleContext';
interface SchoolAccess {
  id: string;
  kid: { id: string; nickname: string };
  institution: { id: string; name: string };
}
interface PublicReview {
  id: string;
  action: string;
  result: { points?: number; level?: string };
  scheme: { kind: string; maximum?: number };
  feedback: string;
  created_at: string;
}
interface SchoolView {
  access: SchoolAccess;
  lessons: Array<{
    id: string;
    scheduled_starts_at: string;
    scheduled_ends_at: string;
    class: { name: string };
  }>;
  attendance: Array<{ status: string; register: { class_day: { date: string } } }>;
  assignments: Array<{
    assignment: { id: string; title: string; due_at: string };
    state: string;
    review: PublicReview | null;
    revision_id?: string;
    body: {
      text?: string;
      attachment_ids?: string[];
      project_versions?: Array<{ title: string; version: number }>;
    } | null;
  }>;
  reports: Array<{
    id: string;
    starts_on: string;
    ends_on: string;
    comment: string;
    published_at: string;
    snapshot: {
      evidence_coverage: string;
      results: Array<{
        assignment_title: string;
        result: { points?: number; level?: string } | null;
        scheme: { kind: string; maximum?: number };
        feedback: string;
      }>;
      attendance: Array<{ status: string }>;
    };
  }>;
  notices: Array<{
    id: string;
    status: string;
    acknowledged_at: string | null;
    notice: { title: string; body: string; created_at: string };
  }>;
}
const inviteSchema = z.object({
  token: z.string().trim().min(40).max(100),
  confirmed: z.boolean().refine((v) => v, 'Confirm your relationship before accepting.'),
});
const err = (v: unknown, t: PortalTranslate) =>
  v instanceof Error ? v.message : t('The school information could not load. Try again.');
export function SchoolLearningPage({ request = api }: { request?: typeof api } = {}) {
  const { t, locale } = usePortalLocale();
  const numberLocale = PORTAL_NUMBER_LOCALE[locale];
  const subject = useUserSubject();
  const client = useQueryClient(),
    access = useQuery({
      queryKey: ['school-learning-access', subject],
      queryFn: () => request<SchoolAccess[]>('/families/me/school-learning', { principal: 'user' }),
      retry: false,
      staleTime: 0,
      refetchOnWindowFocus: 'always',
    }),
    [selected, setSelected] = useState(''),
    [image, setImage] = useState<{ mime: string; data_base64: string; accessId: string } | null>(
      null,
    ),
    [evidenceError, setEvidenceError] = useState('');
  const token = new URLSearchParams(window.location.hash.replace(/^#/, '')).get('invite') ?? '';
  const form = useForm<z.infer<typeof inviteSchema>>({
    resolver: zodResolver(inviteSchema),
    defaultValues: { token, confirmed: false },
  });
  const accept = useMutation({
    mutationFn: (values: z.infer<typeof inviteSchema>) =>
      request<{ id: string }>('/families/me/school-learning/accept', {
        method: 'POST',
        principal: 'user',
        body: { token: values.token, relationship_confirmed: values.confirmed },
      }),
    onSuccess: async (r) => {
      form.reset({ token: '', confirmed: false });
      setSelected(r.id);
      history.replaceState(history.state, '', window.location.pathname + window.location.search);
      await client.invalidateQueries({ queryKey: ['school-learning-access'] });
    },
  });
  const id = selected || access.data?.[0]?.id || '';
  const view = useQuery({
    queryKey: ['school-learning', id, subject],
    queryFn: () => request<SchoolView>(`/families/me/school-learning/${id}`, { principal: 'user' }),
    enabled: Boolean(id),
    retry: false,
    staleTime: 0,
    refetchOnWindowFocus: 'always',
  });
  const ack = useMutation({
    mutationFn: (receipt: string) =>
      request(`/families/me/school-learning/notices/${receipt}/acknowledge`, {
        method: 'POST',
        principal: 'user',
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['school-learning', id] }),
  });
  useEffect(() => {
    if (access.data && selected && !access.data.some((a) => a.id === selected)) setSelected('');
  }, [access.data, selected]);
  const d =
    view.isError || access.isError || !access.data?.some((a) => a.id === id)
      ? undefined
      : view.data;
  return (
    <div className="space-y-6">
      <header className="card-base space-y-3">
        <p className="eyebrow eyebrow-sky">{t('From your child’s school')}</p>
        <h1 className="section-heading">{t('School learning')}</h1>
        <p className="lead-text">
          {t(
            'See published learning, attendance and reports for a verified school relationship. This access keeps the school and responsible teacher in control of the student account.',
          )}
        </p>
      </header>
      {request === api && <LearningDemoEntry perspective="family" />}
      <details className="card-base" open={!access.data?.length}>
        <summary className="font-bold">{t('School invitations')}</summary>
        <form className="card-base space-y-4" onSubmit={form.handleSubmit((v) => accept.mutate(v))}>
          <h2 className="section-title">{t('Accept a guardian invitation')}</h2>
          <fieldset disabled={accept.isPending} className="space-y-4">
            <label className="block">
              <span className="field-label">{t('School invitation code')}</span>
              <input className="input-k12" autoComplete="off" {...form.register('token')} />
            </label>
            <label className="flex gap-3">
              <input type="checkbox" {...form.register('confirmed')} />
              <span>
                {t(
                  'I am authorised to receive this child’s published information, and I am signing in with the email the school verified.',
                )}
              </span>
            </label>
            {Object.values(form.formState.errors).map((e, i) => (
              <p key={i} role="alert" className="text-brand-coral">
                {t(String(e.message))}
              </p>
            ))}
            <button className="btn-pill-primary">
              {accept.isPending ? t('Checking invitation…') : t('Accept school invitation')}
            </button>
          </fieldset>
          <p className="text-sm text-slate2">
            {t(
              'This does not move the student to your Family, grant Study consent or change Stars or payments. Ask the school to revoke access if the relationship changes.',
            )}
          </p>
          {accept.isError && (
            <p role="alert" className="text-brand-coral">
              {err(accept.error, t)}
            </p>
          )}
          {accept.isSuccess && (
            <p role="status" className="text-brand-mint">
              {t('School relationship verified.')}
            </p>
          )}
        </form>
      </details>
      {access.isLoading && <p role="status">{t('Loading school relationships…')}</p>}
      {access.isError && <p role="alert">{err(access.error, t)}</p>}
      {access.data && !access.data.length && (
        <section className="card-base">
          <h2 className="section-title">{t('No verified school relationships yet')}</h2>
          <p>
            {t(
              'Ask your school or tutor for an invitation. A tuition payment or an enquiry does not automatically give learning access.',
            )}
          </p>
        </section>
      )}
      {Boolean(access.data?.length) && (
        <section className="card-base">
          <label className="block">
            <span className="field-label">{t('Child and school')}</span>
            <select
              className="input-k12"
              value={id}
              onChange={(e) => {
                setImage(null);
                setSelected(e.target.value);
              }}
            >
              {access.data?.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.kid.nickname} · {a.institution.name}
                </option>
              ))}
            </select>
          </label>
        </section>
      )}
      {view.isLoading && id && <p role="status">{t('Loading published school information…')}</p>}
      {view.isError && (
        <section className="card-base">
          <p role="alert">
            {t(
              'This school relationship is unavailable or has been revoked. Published information cannot be retrieved with this access.',
            )}
          </p>
          <button
            className="btn-pill-secondary"
            onClick={() => {
              void access.refetch();
              void view.refetch();
            }}
          >
            {t('Refresh access')}
          </button>
        </section>
      )}
      {d && (
        <>
          <section className="card-base space-y-3">
            <h2 className="section-title">{d.access.kid.nickname} · {t('Upcoming lessons')}</h2>
            {d.lessons.map((l) => (
              <p key={l.id}>
                {l.class.name} · {new Date(l.scheduled_starts_at).toLocaleString(numberLocale)} –{' '}
                {new Date(l.scheduled_ends_at).toLocaleTimeString(numberLocale)}
              </p>
            ))}
            {!d.lessons.length && <p>{t('No lessons scheduled in the next two weeks.')}</p>}
          </section>
          <section className="card-base space-y-4">
            <h2 className="section-title">{t('Assigned work and published feedback')}</h2>
            {d.assignments.map((a) => (
              <article
                key={a.assignment.id}
                className="rounded-2xl border border-hairline p-4 space-y-2"
              >
                <h3 className="font-bold">{a.assignment.title}</h3>
                <p>
                  {t('Due {date}', {
                    date: new Date(a.assignment.due_at).toLocaleString(numberLocale),
                  })}{' '}
                  ·{' '}
                  {a.state.replaceAll('_', ' ')}
                </p>
                {a.review ? (
                  <>
                    <p className="font-bold">
                      {a.review.action === 'excused'
                        ? t('Excused')
                        : a.review.action === 'returned'
                          ? t('Returned for revision')
                          : a.review.result.points !== undefined
                            ? `${a.review.result.points} / ${a.review.scheme.maximum}`
                            : (a.review.result.level ?? t('Feedback published'))}
                    </p>
                    <p className="whitespace-pre-wrap">{a.review.feedback}</p>
                    {a.body && (
                      <details>
                        <summary className="font-semibold">{t('Published submitted response')}</summary>
                        <p className="mt-3 whitespace-pre-wrap">{a.body.text}</p>
                        {a.body.attachment_ids?.map((file, i) => (
                          <button
                            key={file}
                            className="btn-pill-secondary"
                            onClick={async () => {
                              setImage(null);
                              try {
                                const image = await request<{ mime: string; data_base64: string }>(
                                  `/families/me/school-learning/${id}/revisions/${a.revision_id}/images/${file}`,
                                  { principal: 'user' },
                                );
                                setImage({ ...image, accessId: id });
                              } catch {
                                setEvidenceError(
                                  t(
                                    'This published image is unavailable. Refresh your school access.',
                                  ),
                                );
                              }
                            }}
                          >
                            {t('View published image {number}', { number: i + 1 })}
                          </button>
                        ))}
                        {a.body.project_versions?.map((p, i) => (
                          <p key={i}>
                            {p.title} · {t('saved version {version}', { version: p.version })}
                          </p>
                        ))}
                      </details>
                    )}
                  </>
                ) : (
                  <p>{t('No published feedback yet. Drafts and private teacher notes stay private.')}</p>
                )}
              </article>
            ))}
            {!d.assignments.length && <p>{t('No published assignments in the current class scope.')}</p>}
          </section>
          <section className="card-base space-y-3">
            <h2 className="section-title">{t('Attendance')}</h2>
            {d.attendance.map((a, i) => (
              <p key={i}>
                {a.register.class_day.date.slice(0, 10)} · {a.status}
              </p>
            ))}
            {!d.attendance.length && <p>{t('No attendance records available.')}</p>}
          </section>
          <section className="card-base space-y-4">
            <h2 className="section-title">{t('Published progress reports')}</h2>
            {d.reports.map((r) => (
              <article key={r.id} className="rounded-2xl border border-hairline p-4 space-y-3">
                <h3 className="font-bold">
                  {r.starts_on.slice(0, 10)} – {r.ends_on.slice(0, 10)}
                </h3>
                <p>
                  {t('Published {date}', { date: new Date(r.published_at).toLocaleString(numberLocale) })}
                </p>
                <p className="whitespace-pre-wrap">{r.comment}</p>
                <p>{r.snapshot.evidence_coverage}</p>
                {r.snapshot.results.map((g, i) => (
                  <p key={i}>
                    {g.assignment_title} ·{' '}
                    {g.result?.points !== undefined
                      ? `${g.result.points} / ${g.scheme.maximum}`
                      : (g.result?.level ?? t('Feedback only'))}{' '}
                    · {g.feedback}
                  </p>
                ))}
                <p>
                  {t('{count} finalised attendance records in this report.', {
                    count: r.snapshot.attendance.length,
                  })}
                </p>
              </article>
            ))}
            {!d.reports.length && (
              <p>{t('The school has not published a report for this relationship yet.')}</p>
            )}
          </section>
          <section className="card-base space-y-4">
            <h2 className="section-title">{t('School notices')}</h2>
            {d.notices.map((n) => (
              <article key={n.id} className="rounded-2xl border border-hairline p-4 space-y-3">
                <h3 className="font-bold">{n.notice.title}</h3>
                <p className="whitespace-pre-wrap">{n.notice.body}</p>
                <p>
                  {t('Available in your school inbox')} · {n.status}
                </p>
                {!n.acknowledged_at && (
                  <button
                    className="btn-pill-secondary"
                    disabled={ack.isPending}
                    onClick={() => ack.mutate(n.id)}
                  >
                    {t('Acknowledge notice')}
                  </button>
                )}
              </article>
            ))}
            {!d.notices.length && <p>{t('No school notices in this view.')}</p>}
            {ack.isError && <p role="alert">{err(ack.error, t)}</p>}
          </section>
        </>
      )}
      {d && image && image.accessId === id && (
        <img
          alt={t('Published submitted evidence')}
          className="max-h-[600px] max-w-full rounded-2xl object-contain"
          src={`data:${image.mime};base64,${image.data_base64}`}
        />
      )}
      {evidenceError && <p role="alert">{evidenceError}</p>}
    </div>
  );
}
